import { createTransform } from 'redux-persist'
import {
    initialState as sduiInitialState,
    type SDUIState,
    type SDUIScreenEntry,
} from './slices/it-process/application-management/sdui/sduiSlice'
import {
    initialState as detailsTabStripsInitialState,
    type DetailsTabStripsState,
} from './slices/it-process/application-management/dts/detailsTabStripsSlice'

/**
 * Persist ONLY the editable field state (value/seeded/dirty) + the active key —
 * never the uiSchema/metadata/RTK cache. On reload the schema is always refetched
 * (RTK Query) and `upsertScreen` refills it, keeping the rehydrated dirty values
 * while non-dirty fields re-seed from the restored selection. This survives an F5
 * without risking stale-schema drift.
 */
type PersistedSdui = {
    activeScreenKey: string | null
    screensByKey: Record<string, { fields: SDUIScreenEntry['fields'] }>
}

export const sduiPersistTransform = createTransform<SDUIState, PersistedSdui>(
    (inbound) => ({
        activeScreenKey: inbound.activeScreenKey,
        screensByKey: Object.fromEntries(
            Object.entries(inbound.screensByKey).map(([key, entry]) => [
                key,
                { fields: entry.fields },
            ]),
        ),
    }),
    // redux-persist's autoMergeLevel1 HARD-SETS the whole slice to this value
    // (it does not deep-merge within a slice), so we must return a COMPLETE
    // SDUIState — start from initial and overlay the restored key + fields.
    // Returning a partial drops newRecordByKey/loadingActions/etc. and crashes
    // reducers (e.g. enterNewRecordMode) that write to them after rehydrate.
    (outbound): SDUIState => ({
        ...sduiInitialState,
        activeScreenKey: outbound?.activeScreenKey ?? null,
        screensByKey: Object.fromEntries(
            Object.entries(outbound?.screensByKey ?? {}).map(
                ([key, partial]) => [
                    key,
                    {
                        uiSchema: [],
                        metadata: {
                            version: '1.0.0',
                            screenId: key,
                            layoutType: 'grid',
                            cacheKey: key,
                        },
                        fields: partial?.fields ?? {},
                        fieldErrors: {},
                        isLoading: false,
                        error: null,
                    },
                ],
            ),
        ),
    }),
    { whitelist: ['sdui'] },
)

/**
 * Persist ONLY the per-DTS selected rows (the binding source) from
 * detailsTabStrips — tabsList and transient UI state stay fresh (refetched on
 * mount). Restoring selection means bound fields re-prefill after a reload.
 */
type PersistedDts = {
    selectedRowsByDts: DetailsTabStripsState['selectedRowsByDts']
}

export const detailsTabStripsPersistTransform = createTransform<
    DetailsTabStripsState,
    PersistedDts
>(
    (inbound) => ({ selectedRowsByDts: inbound.selectedRowsByDts }),
    // redux-persist's autoMergeLevel1 HARD-SETS the whole slice to this value
    // (it does not deep-merge within a slice), so we must return a COMPLETE
    // DetailsTabStripsState — start from initial and overlay the restored
    // selection. Returning only { selectedRowsByDts } would drop submenuHistory/
    // tabsList/etc. and crash reducers that read them.
    (outbound): DetailsTabStripsState => ({
        ...detailsTabStripsInitialState,
        selectedRowsByDts: outbound?.selectedRowsByDts ?? {},
    }),
    { whitelist: ['dts'] },
)
